import { proxy } from "valtio";

import { normalizeInviteCode } from "./inviteCode";

/**
 * 邀请码内存 store —— 供 UI 响应式读取（入口行的两态、填码弹层的预填值）。
 *
 * 与 cookie（{@link import("./inviteCode").INVITE_CODE_COOKIE_KEY}）的分工：
 * - cookie 只存**链接**带来的码，负责跨会话存活（点链接与完成注册常不在同一次会话）
 * - store 存当前生效的码（链接码或手动码），负责当前页面响应式渲染
 *
 * 手动填的码只在 store 里，刷新即丢（PRD 4.2.3，见 `applyManualInviteCode`）。
 * 有码即视为「已应用」：链接码被后端判无效时直接清码（`useInviteCodeCapture`），
 * 不再有「码在但没生效」的中间态。
 */
const inviteCodeStore = proxy<{
  /** 当前生效的邀请码；null 表示没有码。 */
  code: string | null;
  /** 来源：`link` = 从邀请链接/深链自动捕获；`manual` = 用户在填码弹层手动输入。 */
  source: "link" | "manual" | null;
}>({
  code: null,
  source: null,
});

/** 只读快照供非 React 上下文（请求头合并等）读取。 */
export function getInviteCodeFromStore(): string | null {
  return inviteCodeStore.code;
}

/** 来源标记，供埋点判断。 */
export function getInviteCodeSourceFromStore(): "link" | "manual" | null {
  return inviteCodeStore.source;
}

/** 写入邀请码（**不改大小写**，后端精确匹配）。空值等价于清除。 */
export function setInviteCodeInStore(
  value: string | null | undefined,
  source: "link" | "manual",
): void {
  const normalized = normalizeInviteCode(value);
  inviteCodeStore.code = normalized;
  inviteCodeStore.source = normalized ? source : null;
}

/** 清除（用户在填码弹层清空、或注册完成后收尾）。 */
export function clearInviteCodeInStore(): void {
  inviteCodeStore.code = null;
  inviteCodeStore.source = null;
}

/** React 组件用 `useSnapshot(inviteCodeState)` 订阅。 */
export const inviteCodeState = inviteCodeStore;
